import type { Customer as CustomerWithNullableAddress } from 'Customers/types'
import { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'
import { SimpleModal, Box } from '@sequencehq/core-components'
import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import {
  CreateCustomerError,
  useCreateCustomer,
  PerformCreateCustomer
} from 'modules/Customers/hooks/useCreateCustomer'
import { CustomerFormErrorBanner } from 'Customer/components/CustomerFormErrorBanner'
import { CreateCustomer } from 'modules/Customers/types'
import {
  CustomerFormFields,
  CustomerFormFieldsData,
  useCustomerFormFields
} from 'Customer/components/CustomerFormFields'
import {
  customFieldsFromRecord,
  customFieldsToRecord
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api/utils'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { CountriesAlpha2 } from '@sequencehq/api'
import { statesForCountry } from '@sequencehq/api/utils/commonEnums'
import { getPrimaryBillingContact } from 'CustomerContacts/index'
import { useLogEvent } from 'lib/postHog/useLogEvent'
import { getBillingAddress } from '@sequencehq/api/utils/commonModels'

interface Props {
  onClose: () => void
  onSuccess?: (customer: CustomerWithNullableAddress) => void
  createCustomer?: PerformCreateCustomer
  prefill?: Partial<CreateCustomer>
}

export const CreateCustomerForm = (props: Props) => {
  const {
    initialData,
    lockedAliasCount,
    createError,
    showValidationErrors,
    failedSubmitCount,
    errorBannerRef,
    setIsValid,
    handleFormChange,
    handleCreate
  } = useCreateCustomerForm(props)

  const { fields, customProperties } = useCustomerFormFields({
    initialData,
    onChange: handleFormChange,
    onValidationStateChange: setIsValid,
    isANewCustomer: true,
    showValidationErrors
  })

  return (
    <SimpleModal
      isOpen
      data-testid="createCustomer.drawer"
      variant="drawer"
      title="Create customer"
      cancelButtonText="Cancel"
      submitButtonText="Create customer"
      submittingButtonText="Creating customer..."
      onClose={props.onClose}
      onSubmit={handleCreate}
      // On success only onSuccess runs (it navigates/unmounts this drawer);
      // also firing onClose would repeat relative navigations in consumers.
      closeOnUpdate={false}
    >
      <Box ref={errorBannerRef} marginBottom={createError ? '16px' : 0}>
        <CustomerFormErrorBanner operation="CREATE" error={createError} />
      </Box>
      <CustomerFormFields failedSubmitCount={failedSubmitCount}>
        <CustomerFormFields.AccountInformation
          data-testid="createCustomerForm.accountInformation"
          legalNameField={fields.legalName}
          contactsField={fields.contacts}
          autoFocus
        />
        <CustomerFormFields.BillingInformation
          data-testid="createCustomerForm.billingInformation"
          addressFields={fields}
        />
        <CustomerFormFields.Tax
          data-testid="createCustomerForm.tax"
          taxStatusField={fields.taxStatus}
          taxRegistrationsField={fields.taxRegistrations}
          defaultCountry={fields['address.country'].value}
          defaultState={fields['address.state'].value}
        />
        <CustomerFormFields.OtherInformation
          data-testid="createCustomerForm.otherInformation"
          labelField={fields.label}
          parentIdField={fields.parentId}
          aliasesField={fields.aliases}
          customProperties={customProperties}
          lockedAliasCount={lockedAliasCount}
        />
      </CustomerFormFields>
    </SimpleModal>
  )
}

const useCreateCustomerForm = (props: Props) => {
  const { prefill } = props
  const { logEvent } = useLogEvent()

  const createCustomer = useCreateCustomer({
    performCreateCustomer: props.createCustomer
  })

  useEffect(() => {
    logEvent({ eventName: 'create_customer_form_opened' })
  }, [])

  const [createError, setCreateError] = useState<CreateCustomerError>()
  const [isValid, setIsValid] = useState(true)
  const [failedSubmitCount, setFailedSubmitCount] = useState(0)
  const showValidationErrors = failedSubmitCount > 0
  const formDataRef = useRef<CustomerFormFieldsData | null>(null)
  const errorBannerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (createError) {
      errorBannerRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [createError])

  const { data: merchant } = useQuery(
    dashboard20240730Client.getMerchantForSequenceAccount
  )

  const merchantCountry = (merchant?.address.country ?? 'GB') as CountriesAlpha2

  const initialData = useMemo((): CustomerFormFieldsData => {
    const primaryContact = getPrimaryBillingContact(prefill?.contacts ?? [])
    const prefillAddress = getBillingAddress(prefill?.addresses)
    return {
      legalName: prefill?.legalName ?? '',
      contacts: [
        {
          id: crypto.randomUUID(),
          name: primaryContact?.name ?? '',
          email: primaryContact?.email ?? '',
          purposes: ['BILLING'],
          notificationSubscriptions: ['BILLING']
        }
      ],
      address: {
        country: prefillAddress?.country ?? merchantCountry,
        line1: prefillAddress?.line1 ?? '',
        line2: prefillAddress?.line2,
        town: prefillAddress?.town ?? '',
        state: prefillAddress?.state,
        postcode: prefillAddress?.postcode ?? ''
      },
      taxStatus: prefill?.taxStatus ?? 'TAXED',
      aliases: prefill?.customerAliases?.length
        ? prefill.customerAliases
        : [''],
      label: prefill?.label ?? '',
      parentId: prefill?.parentId,
      taxRegistrations:
        prefill?.taxRegistrations?.map(registration => ({
          id: registration.id || crypto.randomUUID(),
          registrationId: registration.registrationId,
          country: registration.country || merchantCountry,
          state: registration.state,
          identifier: registration.identifier
        })) ?? [],
      customProperties: customFieldsFromRecord(prefill?.customProperties)
    }
  }, [prefill, merchantCountry])

  const lockedAliasCount = (prefill?.customerAliases ?? []).filter(
    Boolean
  ).length

  const handleFormChange = useCallback((data: CustomerFormFieldsData) => {
    formDataRef.current = data
  }, [])

  const handleCreate = async () => {
    setCreateError(undefined)

    if (!isValid) {
      setFailedSubmitCount(count => count + 1)
      return false
    }

    const data = formDataRef.current ?? initialData
    const countryStates = statesForCountry(
      data.address.country as CountriesAlpha2
    )

    const customerPayload: CreateCustomer = {
      legalName: data.legalName.trim(),
      contacts: data.contacts.map(c => ({
        name: c.name.trim(),
        email: c.email.trim(),
        purposes: c.purposes,
        notificationSubscriptions: c.notificationSubscriptions
      })),
      address: {
        country: data.address.country as CountriesAlpha2,
        line1: data.address.line1.trim(),
        line2: data.address.line2?.trim() || undefined,
        town: data.address.town.trim(),
        state:
          data.address.state && countryStates.includes(data.address.state)
            ? data.address.state
            : undefined,
        postcode: data.address.postcode.trim()
      },
      taxStatus: data.taxStatus as CreateCustomer['taxStatus'],
      label: data.label || undefined,
      parentId: data.parentId || undefined,
      customerAliases: data.aliases,
      taxRegistrations: data.taxRegistrations,
      customProperties: customFieldsToRecord(data.customProperties)
    }

    const result = await createCustomer(customerPayload)

    if (result?.error) {
      setCreateError(result.error)
      return false
    }

    if (result?.customer) {
      props.onSuccess?.(result.customer)
      return true
    }

    return false
  }

  return {
    initialData,
    lockedAliasCount,
    createError,
    showValidationErrors,
    failedSubmitCount,
    errorBannerRef,
    setIsValid,
    handleFormChange,
    handleCreate
  }
}
